import React from 'react'
import { Modal, Button, Typography, Box, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'

interface SeasonProductToggleConfirmationModalProps {
	open: boolean
	onClose: () => void
	onConfirm: () => void
	/** 바꾸려는 스위치 이름. 프리오더 판매 · 얼리버드 · 재시작 판매 */
	label: string
	/** 켜는 중이면 true */
	next: boolean
}

/**
 * 시즌 상품 상세의 스위치(프리오더 판매 · 얼리버드 · 재시작 판매) 확인.
 *
 * 스위치 하나가 shop 의 가격 구조와 판매 상태를 통째로 바꾼다. 저장 즉시 반영되므로
 * 누르는 순간 한 번 묻는다.
 */
export const SeasonProductToggleConfirmationModal: React.FC<
	SeasonProductToggleConfirmationModalProps
> = ({ open, onClose, onConfirm, label, next }) => {
	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="toggle-confirmation-title"
			aria-describedby="toggle-confirmation-description"
		>
			<Box component="div" sx={styles.modalContent}>
				<IconButton onClick={onClose} sx={styles.closeButton} aria-label="close">
					<CloseIcon />
				</IconButton>
				<StorefrontOutlinedIcon sx={styles.icon} />
				<Typography
					id="toggle-confirmation-title"
					variant="h6"
					component="h2"
					sx={styles.title}
				>
					{label} {next ? '켜기' : '끄기'}
				</Typography>
				<Typography
					id="toggle-confirmation-description"
					variant="body1"
					sx={styles.description}
				>
					<strong>{label}</strong>를 {next ? '켭니다' : '끕니다'}.
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={styles.warning}>
					이 설정은 저장하면 shop 에 바로 영향을 줍니다. 정말 변경하시겠습니까?
				</Typography>
				<Box sx={styles.buttonContainer}>
					<Button variant="text" onClick={onClose} sx={styles.cancelButton}>
						취소
					</Button>
					<Button variant="contained" onClick={onConfirm} sx={styles.confirmButton}>
						변경
					</Button>
				</Box>
			</Box>
		</Modal>
	)
}

const styles = {
	modalContent: {
		position: 'absolute',
		top: '50%',
		left: '50%',
		transform: 'translate(-50%, -50%)',
		width: '400px',
		maxWidth: 'calc(100vw - 32px)',
		backgroundColor: mono.surface,
		border: `1px solid ${mono.line}`,
		boxShadow: '0 30px 60px rgba(20, 24, 32, 0.12)',
		padding: '32px',
		borderRadius: 0,
		textAlign: 'left',
		fontFamily: boFont.ui,
		outline: 'none',
	},
	closeButton: {
		position: 'absolute',
		right: '8px',
		top: '8px',
	},
	icon: {
		fontSize: '28px',
		color: mono.dim,
		marginBottom: '16px',
	},
	title: {
		marginBottom: '12px',
		fontSize: 18,
		fontWeight: 400,
		color: mono.ink,
	},
	description: {
		marginBottom: '8px',
		fontSize: 14,
		fontWeight: 300,
		color: mono.ink,
		lineHeight: 1.6,
	},
	warning: {
		marginBottom: '24px',
		fontSize: 13,
		fontWeight: 300,
		color: mono.dim,
		lineHeight: 1.6,
	},
	buttonContainer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: '8px',
		paddingTop: '16px',
		borderTop: `1px solid ${mono.line}`,
	},
	cancelButton: {
		minWidth: '80px',
	},
	confirmButton: {
		minWidth: '100px',
	},
	deleteButton: {
		minWidth: '100px',
	},
}
